import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { useProgress } from "./lib/stores/useProgress";
import { useProfile } from "./lib/stores/useProfile";
import { useAuth } from "./lib/stores/useAuth";
import { useRocketanks } from "./lib/stores/useRocketanks";
import { useGraphics } from "./lib/stores/useGraphics";
import { useAudio } from "./lib/stores/useAudio";
import { warmupAudio, getSharedAudioContext } from "./lib/audio/proceduralSfx";
import { notifyUserInteraction } from "./lib/audio/menuTheme";
import { SplashScreen } from "./components/SplashScreen";
import { initializeI18n, useI18n, useT } from "./lib/i18n";
import { LoadingState } from "./components/rk/primitives";
import { initCapacitor, hideSplashScreen, exitApp, getNetworkStatus, onNetworkChange } from "./lib/capacitor";
import { syncPlatformAgeSignal } from "./lib/ageSignals";
import { useNotifications } from "./lib/stores/useNotifications";
import { dispatchBack } from "./lib/backButton";
import { getCurrentRoom } from "./lib/colyseus/client";
import { apiFetch, apiUrl, closeInAppBrowser } from "./lib/apiBase";
import { useSubscription } from "./lib/stores/useBattlePass";
import { getPlayerId } from "./lib/utils";
import { MusicAdapter } from "./game/MusicAdapter";
import { adsService } from "./lib/ads/AdsService";
import { useSofa } from "./lib/sofa/useSofa";
import { notifyAdsStateChange } from "./lib/ads/useAds";
import "@fontsource/inter";

// Code splitting - lazy load heavy components
const Game = lazy(() => import("./game/Game").then(m => ({ default: m.Game })));
const StoryGame = lazy(() => import("./game/StoryGame").then(m => ({ default: m.StoryGame })));
const StoryMode = lazy(() => import("./pages/story-mode").then(m => ({ default: m.StoryMode })));
const AdminPage = lazy(() => import("./pages/admin"));
const SofaMode = lazy(() => import("./game/sofa/SofaMode").then(m => ({ default: m.SofaMode })));
const PoliticaPrivacidade = lazy(() => import("./pages/politica-privacidade"));
const TermosUso = lazy(() => import("./pages/termos-uso"));

// Loading component for Suspense
function LoadingScreen() {
  // Primeira tela que o jogador vê no boot: precisa sair traduzida,
  // não em português para todo mundo.
  const t = useT();
  return (
    <div className="fixed inset-0 bg-slate-900 flex items-center justify-center">
      <LoadingState label={t("common.loading")} size="lg" className="text-cyan-400" labelClassName="text-lg" />
    </div>
  );
}

const resolvePresenceGame = (currentPhase: string, multiplayer: boolean): "story" | "pvp" | "lobby" => {
  if (currentPhase === "storyIntro" || currentPhase === "storyPlaying" || currentPhase === "storyVictory") {
    return "story";
  }
  if (currentPhase === "playing" || currentPhase === "paused" || currentPhase === "waveComplete" || currentPhase === "gameOver") {
    return multiplayer ? "pvp" : "pvp";
  }
  return "lobby";
};

function App() {
  const loadFromServer = useProgress((state) => state.loadFromServer);
  const loadProfileFromServer = useProfile((state) => state.loadFromServer);
  const profile = useProfile((state) => state.profile);
  const refreshUser = useAuth((state) => state.refreshUser);
  const isAuthenticated = useAuth((state) => state.isAuthenticated);
  const { phase, isMultiplayer } = useRocketanks();
  const sofaActive = useSofa((s) => s.active);
  const [showSplash, setShowSplash] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const presenceGameRef = useRef<"story" | "pvp" | "lobby">("lobby");

  // Track if we're in story mode (to handle paused/waveComplete/gameOver in story context)
  const isInStoryMode = useRef(false);
  
  // Track previous auth state to detect logout
  const wasAuthenticated = useRef(isAuthenticated);

  // Verificar se está na página de admin
  const isAdminPage = window.location.pathname === "/admin";
  const isPoliticaPrivacidade = window.location.pathname === "/politica-privacidade";
  const isTermosUso = window.location.pathname === "/termos-uso";

  // Handler para quando o splash é clicado
  const handleSplashStart = () => {
    setShowSplash(false);
    // Esconder o SplashScreen nativo do Capacitor (se houver)
    hideSplashScreen();
  };

  // Sinal de idade da Play Store (Lei Felca, arts. 12-13). Só depois do login,
  // porque o sinal é gravado na conta — e a cada login, porque o jogador pode
  // ter passado a compartilhar a faixa (ou um responsável pode tê-la definido)
  // entre uma sessão e outra. No web e sem o plugin isso é um no-op.
  useEffect(() => {
    if (!isAuthenticated) return;
    void syncPlatformAgeSignal().then((status) => {
      if (status === "reported") {
        console.log("[App] Sinal de idade da loja registrado");
      }
    });

    // A inscrição de push nasce presa ao id de convidado (é o que existe antes
    // do login). Reinscrever aqui move o registro para a conta real — senão o
    // servidor mandaria os avisos da conta para um id que ninguém consulta.
    const { permissionStatus, subscribeToPush } = useNotifications.getState();
    if (permissionStatus === "granted") void subscribeToPush();
  }, [isAuthenticated]);

  // Detect logout and show splash again
  useEffect(() => {
    // If user was authenticated and now is not (logout happened)
    if (wasAuthenticated.current && !isAuthenticated && !showSplash) {
      console.log("[App] User logged out, showing splash screen");
      setShowSplash(true);
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, showSplash]);

  // Inicializar Capacitor (plugins nativos: StatusBar, BackButton, Network, etc.)
  useEffect(() => {
    initCapacitor().then(() => {
      // Verificar status de rede inicial
      setIsOffline(!getNetworkStatus());
    });

    // Escutar mudancas de rede
    const unsubscribeNetwork = onNetworkChange((connected) => {
      setIsOffline(!connected);
      if (connected) {
        console.log("[App] Network restored");
      } else {
        console.warn("[App] Network lost");
      }
    });

    // Escutar back button do Android (via Capacitor)
    const handleBackButton = () => {
      // Handlers registrados (submenus, lobby, telas de fim de partida)
      // têm prioridade — sem isto o back fechava o app de dentro de
      // qualquer submenu, porque todos mantêm phase === "menu".
      if (dispatchBack()) return;

      const currentPhase = useRocketanks.getState().phase;

      if (currentPhase === "playing" || currentPhase === "storyPlaying") {
        // Durante gameplay: pausar
        useRocketanks.getState().setPaused(true);
      } else if (currentPhase === "paused") {
        // Pausado: retomar
        useRocketanks.getState().setPaused(false);
      } else if (currentPhase === "menu") {
        // No menu principal: sair do app
        exitApp();
      } else if (currentPhase === "story") {
        // Na selecao de historia: voltar ao menu
        useRocketanks.getState().setPhase("menu");
      }
      // Outros estados: ignorar back button
    };

    window.addEventListener("capacitor:backButton", handleBackButton);

    // Escutar app background/foreground (via Capacitor)
    const handleAppStateChange = (event: Event) => {
      const { isActive } = (event as CustomEvent).detail;

      if (isActive) {
        // App voltou ao foreground: resumir audio e verificar conexao
        getSharedAudioContext(); // Auto-resume se suspended
        notifyUserInteraction();

        // Verificar se Colyseus ainda esta conectado
        const room = getCurrentRoom();
        if (room && !room.connection.isOpen) {
          console.warn("[App] Colyseus connection lost while backgrounded");
          // Disparar evento para que o jogo trate a desconexao
          window.dispatchEvent(new CustomEvent("colyseus:disconnected"));
        }
      }
    };

    window.addEventListener("capacitor:appStateChange", handleAppStateChange);

    // Retorno do checkout do Stripe no app nativo.
    //
    // No web o Stripe redireciona a própria página e o app inteiro recarrega
    // com o resultado; no app o checkout roda numa Custom Tab e a URL do
    // WebView nunca muda. O servidor devolve o jogador via /pay/return, que
    // reenvia para com.gamelabs.rocketanks://payment?... — sem tratar isso, a
    // compra terminava numa aba do navegador e o app ficava exatamente como
    // antes, sem cristais e sem aviso.
    const handlePaymentReturn = (event: Event) => {
      const { url } = (event as CustomEvent<{ url?: string }>).detail ?? {};
      if (!url) return;

      let parsed: URL;
      try {
        parsed = new URL(url);
      } catch {
        return;
      }
      if (parsed.host !== "payment") return;

      // A Custom Tab fica empilhada atrás do app depois do redirect.
      closeInAppBrowser();

      const cancelled =
        parsed.searchParams.get("payment") === "cancelled" ||
        parsed.searchParams.get("subscription") === "canceled";
      if (cancelled) return;

      // O crédito vem por webhook — pode chegar depois do jogador.
      // pollSubscriptionStatus já reconsulta em intervalos; o progresso
      // recarrega junto para os cristais aparecerem sem reiniciar o app.
      useSubscription.getState().pollSubscriptionStatus().finally(() => {
        useProgress.getState().loadFromServer().catch(() => {});
      });
    };

    window.addEventListener("capacitor:urlOpen", handlePaymentReturn);

    return () => {
      unsubscribeNetwork();
      window.removeEventListener("capacitor:backButton", handleBackButton);
      window.removeEventListener("capacitor:appStateChange", handleAppStateChange);
      window.removeEventListener("capacitor:urlOpen", handlePaymentReturn);
    };
  }, []);

  // Inicializar sistema de áudio na primeira interação do usuário
  useEffect(() => {
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso) return;
    
    const initAudio = () => {
      warmupAudio();
      notifyUserInteraction(); // Notificar menuTheme que usuário interagiu
      console.log("[App] Audio system initialized");
    };
    
    // Adicionar listeners para primeira interação
    document.addEventListener("click", initAudio, { once: true });
    document.addEventListener("touchstart", initAudio, { once: true });
    document.addEventListener("keydown", initAudio, { once: true });
    
    return () => {
      document.removeEventListener("click", initAudio);
      document.removeEventListener("touchstart", initAudio);
      document.removeEventListener("keydown", initAudio);
    };
  }, [isAdminPage]);

  // Config global para cache-busting de áudio (musicas e sons)
  useEffect(() => {
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso) return;
    apiFetch("/api/config/audio-cache-bust")
      .then((res) => res.json())
      .then((data) => {
        useAudio.getState().setAudioCacheBuster(!!data.enabled, data.version || "");
      })
      .catch(() => {
        useAudio.getState().setAudioCacheBuster(false, "");
      });
  }, [isAdminPage, isPoliticaPrivacidade, isTermosUso]);

  // Inicialização do AdsService (Web H5 Games Ads + Android AdMob)
  useEffect(() => {
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso) return;
    apiFetch("/api/config/ads")
      .then((res) => res.json())
      .then(async (config) => {
        await adsService.init(config);
        // Sync som com o estado de mute do jogo
        const isMuted = useAudio.getState().isMuted;
        adsService.updateSound(!isMuted);
        notifyAdsStateChange();
      })
      .catch(() => {
        // Ads ficam desligados se config falhar
        adsService.init({ enabled: false, interstitial: { enabled: false }, rewarded: { enabled: false }, preroll: { enabled: false }, testMode: false });
      });
  }, [isAdminPage, isPoliticaPrivacidade, isTermosUso]);

  // Presença do jogador para o mapa administrativo (online/offline)
  useEffect(() => {
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso || showSplash) return;

    const odysId = profile?.odysId || getPlayerId();
    const username = profile?.displayName || profile?.username || "Player";
    if (!odysId) return;

    const reportLocation = () => {
      const currentGame = presenceGameRef.current;
      apiFetch("/api/players/location", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ odysId, username, currentGame }),
      }).catch(() => {});
    };

    const heartbeat = () => {
      const currentGame = presenceGameRef.current;
      apiFetch("/api/players/heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ odysId, currentGame }),
      }).catch(() => {});
    };

    const sendOffline = () => {
      const payload = JSON.stringify({ odysId });
      const url = apiUrl("/api/players/offline");
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
      apiFetch("/api/players/offline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    };

    reportLocation();

    const intervalId = window.setInterval(() => {
      heartbeat();
    }, 60000);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        heartbeat();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", sendOffline);
    window.addEventListener("pagehide", sendOffline);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", sendOffline);
      window.removeEventListener("pagehide", sendOffline);
    };
  }, [isAdminPage, isPoliticaPrivacidade, isTermosUso, showSplash, profile?.odysId, profile?.displayName, profile?.username]);

  // Hook de Auto-Detecção Genial de Hardware (WebGL, Nucleos lógicos e Hz da Tela)
  useEffect(() => {
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso) return;

    const { hasAutoDetected, autoDetectHardware } = useGraphics.getState();
    if (!hasAutoDetected) {
      console.log("[App] First boot detected! Resolving optimized Graphic Presets and Unlocking FPS...");
      autoDetectHardware();
    }
  }, [isAdminPage, isPoliticaPrivacidade, isTermosUso]);

  useEffect(() => {
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso || showSplash) return;
    const currentGame = resolvePresenceGame(phase, isMultiplayer);
    presenceGameRef.current = currentGame;
    const odysId = profile?.odysId || getPlayerId();
    if (!odysId) return;
    apiFetch("/api/players/heartbeat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ odysId, currentGame }),
    }).catch(() => {});
  }, [phase, isMultiplayer, isAdminPage, isPoliticaPrivacidade, isTermosUso, showSplash, profile?.odysId]);
  
  // Update story mode tracker based on story phases
  useEffect(() => {
    if (phase === "storyIntro" || phase === "storyPlaying" || phase === "storyVictory") {
      isInStoryMode.current = true;
    } else if (phase === "story" || phase === "menu" || phase === "playing") {
      isInStoryMode.current = false;
    }
  }, [phase]);

  useEffect(() => {
    // Não carregar progresso na página de admin ou páginas legais
    if (isAdminPage || isPoliticaPrivacidade || isTermosUso) return;
    
    // Inicializar sistema de i18n (carrega taxas de câmbio)
    initializeI18n()
      .then(rates => {
        useI18n.getState().updateExchangeRates(rates);
        console.log("[App] i18n initialized with exchange rates", rates);
      })
      .catch(error => {
        console.warn("[App] i18n initialization failed, using default rates", error);
      });
    
    // Verificar sessão de autenticação existente
    // Isso sincroniza o estado auth do servidor e recarrega dados se necessário
    refreshUser().catch((err) => {
      console.warn("[App] refreshUser failed (user may not be logged in):", err);
    });
    
    // Carregar progresso do servidor com timeout de segurança
    const timeoutId = setTimeout(() => {
      console.warn("loadFromServer timeout - continuando sem dados do servidor");
    }, 5000);

    Promise.all([
      loadFromServer(),
      loadProfileFromServer(),
    ]).finally(() => {
      clearTimeout(timeoutId);
    });

    return () => clearTimeout(timeoutId);
  }, []); // Apenas executar uma vez ao montar

  // Página de admin - lazy loaded
  if (isAdminPage) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AdminPage />
      </Suspense>
    );
  }

  // Página de Política de Privacidade
  if (isPoliticaPrivacidade) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PoliticaPrivacidade />
      </Suspense>
    );
  }

  // Página de Termos de Uso
  if (isTermosUso) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TermosUso />
      </Suspense>
    );
  }

  // Tela de splash (antes de qualquer coisa)
  if (showSplash) {
    return <SplashScreen onStart={handleSplashStart} />;
  }

  // Modo Sofá (2 jogadores, tela dividida): substitui o <Game/> inteiro.
  // O pipeline normal fica desmontado — nada do estado de um jogador local
  // (solo, BR, multiplayer online) é tocado durante a sessão de sofá.
  if (sofaActive) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <SofaMode />
      </Suspense>
    );
  }

  // Se em modo história (seleção de capítulos), renderizar StoryMode
  if (phase === "story") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <StoryMode />
      </Suspense>
    );
  }

  // Se em fases de gameplay de história ou estados de jogo enquanto no modo história
  const storyPhases = ["storyPrologue", "storyIntro", "storyPlaying", "storyVictory"];
  const gameStatePhases = ["paused", "waveComplete", "gameOver"];
  
  if (storyPhases.includes(phase) || (gameStatePhases.includes(phase) && isInStoryMode.current)) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        {/* MusicAdapter global - único ponto de controle de música */}
        <MusicAdapter />
        <StoryGame />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      {/* MusicAdapter global - único ponto de controle de música */}
      <MusicAdapter />
      {isOffline && <OfflineBanner />}
      <Game />
    </Suspense>
  );
}

// Banner de conexao perdida
function OfflineBanner() {
  const t = useT();
  return (
    <div
      role="status"
      className="fixed top-0 left-0 right-0 z-50 bg-red-600/90 text-white text-center py-2 text-sm font-medium safe-area-top"
    >
      {t("ui.offline")}
    </div>
  );
}

export default App;
